import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroCarousel from './components/HeroCarousel';
import CategoryEntries from './components/CategoryEntries';
import MovieCard from '@/components/base/MovieCard';
import SectionTitle from '@/components/base/SectionTitle';
import StateBlock from '@/components/base/StateBlock';
import { mediaList } from '@/mocks/media';
import { useSeo } from '@/hooks/useSeo';

function CardSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="w-full aspect-[2/3] rounded-lg bg-background-200"></div>
      <div className="mt-2.5 h-4 w-3/4 rounded bg-background-200"></div>
      <div className="mt-2 h-3 w-1/2 rounded bg-background-200"></div>
    </div>
  );
}

export default function Home() {
  const [loading, setLoading] = useState(true);

  useSeo({
    title: '看片影视大全 - 最新电影电视剧在线观看平台',
    description:
      '看片网站提供丰富的影视资源，包括最新电影、电视剧和综艺节目，支持多种设备在线观看，满足您的观影需求。',
    keywords: '看片,影视平台,电影资源,电视剧观看,在线播放,影视大全,高清电影,影视资讯',
  });

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const latest = [...mediaList].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 10);
  const hot = [...mediaList].sort((a, b) => b.heat - a.heat).slice(0, 10);

  const stats = [
    { value: `${mediaList.length}+`, label: '精选影视资源' },
    { value: '3', label: '内容分类频道' },
    { value: '每日', label: '内容持续更新' },
    { value: '全端', label: '多设备浏览适配' },
  ];

  return (
    <div className="w-full">
      <HeroCarousel />

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <SectionTitle
          title="分类快捷入口"
          subtitle="按频道快速进入你感兴趣的内容"
        />
        <CategoryEntries />
      </div>

      <div className="bg-background-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14">
          <SectionTitle
            title="最新上线"
            subtitle="刚刚更新上架的影视作品"
            actionText="查看全部"
            actionTo="/category/movie"
          />
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {Array.from({ length: 10 }).map((_, i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          ) : latest.length === 0 ? (
            <StateBlock title="暂无相关影视资源" description="内容正在陆续上线，敬请期待。" />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {latest.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <SectionTitle
          title="热门影视精选"
          subtitle="大家都在看的高热度内容"
          actionText="查看全部"
          actionTo="/category/tv"
        />
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
            {Array.from({ length: 10 }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : hot.length === 0 ? (
          <StateBlock title="暂无相关影视资源" description="内容正在陆续上线，敬请期待。" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
            {hot.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      <div className="bg-primary-950 text-background-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-4">
                关于看片影视大全
              </h2>
              <p className="text-sm md:text-base leading-relaxed text-background-100/85 mb-4">
                看片致力于为影视爱好者打造一个清晰、丰富、易用的内容导航平台。我们汇集最新电影、热门电视剧与精彩综艺节目，
                提供多维筛选、关键词搜索与贴心推荐，帮助你在海量内容中快速找到想看的那一部。
              </p>
              <p className="text-sm leading-relaxed text-background-100/70 mb-6">
                本站所有影视内容均为信息展示，不含真实播放、会员或付费功能，所有资料仅用于内容浏览与介绍。
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors"
              >
                了解我们
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {stats.map((item) => (
                <div key={item.label} className="rounded-lg bg-background-50/10 border border-background-50/15 p-5">
                  <p className="text-2xl md:text-3xl font-extrabold text-accent-400">{item.value}</p>
                  <p className="mt-1 text-xs md:text-sm text-background-100/80">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}